import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import { api } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/15 bg-navy-900/60 px-4 py-3 text-sm text-ivory placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function AdminLogin({ onSuccess }) {
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      localStorage.setItem("rcs_admin_token", r.data.token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(errText(err, "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password");
      setOtpSent(true);
      toast.success("Verification code sent to the admin email.");
    } catch (err) {
      toast.error(errText(err, "Could not send the code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { otp, newPassword });
      toast.success("Password updated. Please sign in with your new password.");
      setMode("login");
      setOtp("");
      setNewPassword("");
      setOtpSent(false);
    } catch (err) {
      toast.error(errText(err, "Could not reset the password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-950 px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-gold/20 bg-navy-900/60 p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="flex flex-col items-center text-center">
            <img src="/rcs-logo.png" alt="Roy Career Solutions Private Limited" className="h-16 w-auto" />
            <h1 className="mt-6 font-serif text-2xl text-ivory">Admin CMS</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-500">
              {mode === "login" ? "Restricted Access" : "Password Recovery"}
            </p>
          </div>

          {mode === "login" ? (
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Admin email"
                  aria-label="Admin email"
                  className={`${inputCls} pl-11`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                />
              </div>
              <div className="relative">
                <LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder="Password"
                  aria-label="Password"
                  className={`${inputCls} pl-11 pr-11`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-gold"
                  data-testid="admin-login-toggle-password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                data-testid="admin-forgot-link"
              >
                Forgot password?
              </button>
            </form>
          ) : (
            <div className="mt-8 space-y-4" data-testid="admin-forgot-form">
              <p className="text-center text-xs leading-relaxed text-slate-400">
                A 6-digit verification code will be sent to the registered admin email
                <span className="block mt-1 font-semibold text-gold">suritray@roycareersolutions.com</span>
              </p>
              {!otpSent ? (
                <button onClick={sendOtp} disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="admin-send-otp">
                  {loading ? "Sending…" : "Send Verification Code"}
                </button>
              ) : (
                <form onSubmit={resetPassword} className="space-y-4">
                  <input
                    required
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    aria-label="Verification code"
                    className={`${inputCls} text-center text-lg tracking-[0.5em]`}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    data-testid="admin-otp-input"
                  />
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="New password (min 8 characters)"
                    aria-label="New password"
                    className={inputCls}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="admin-new-password"
                  />
                  <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="admin-reset-submit">
                    {loading ? "Updating…" : "Reset Password"}
                  </button>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={loading}
                    className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                    data-testid="admin-resend-otp"
                  >
                    Resend code
                  </button>
                </form>
              )}
              <button
                type="button"
                onClick={() => setMode("login")}
                className="w-full text-center text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                data-testid="admin-back-to-login"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>
        <p className="mt-6 text-center text-[11px] text-slate-600">
          Roy Career Solutions Private Limited · Authorised access only
        </p>
      </div>
    </div>
  );
}
